import React, {useEffect, useRef} from 'react'
import {MenuItem} from '@mui/material'
import {usePersona} from '../../../store/hooks/persona'
import CustomBox from '../../common/CustomBox'
import {classesIP, Root} from './selectedPersonaStyles'
import Select from '@mui/material/Select'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Overview from './Overview/Overview'
import PersonaDesc from './PersonaDesc.config'
import PersonaHeader from './PersonaHeader'
import {classes} from '../PersonaSummary/SummaryComponentStyles'
import Engagement from './Engagement/Engagement.js'
import Outreach from './Outreach/Outreach.js'
import {AnalyticsHandler} from '../../../shared/handlers/analytics/handlers.js'
import {useFilterListDetail} from '../../../store/hooks/filterListDetail'
import {getPersonaCustomLabel} from '../../Dashboard/helper.js'
import ChatbotCTA from '../ChatbotCTA/ChatbotCTA'
import {StyledSpaceBox} from '../Styles.js'

const SelectedPersona = () => {
  const {personaList} = useFilterListDetail()
  const {personaSidebarOpen, setPersonaSidebarOpen} = usePersona()
  const [personaSection, setPersonaSection] = React.useState('Overview')
  const selectedPersonaRef = useRef(null)

  // This function will handle the custom label for the persona sections
  const handlePersonaCustomLabel = (type, label = null, title = null) => {
    return getPersonaCustomLabel(personaList, title, label, type)
  }

  useEffect(() => {
    if (personaSidebarOpen?.open) {
      selectedPersonaRef?.current?.focus({preventScroll: true})
    }
  }, [personaSidebarOpen])

  // This part will decide which section to render based on the selected section
  const renderContent = () => {
    switch (personaSection) {
      case 'Overview':
        return (
          <Overview filteredPersonaDesc={filteredPersonaDesc} handlePersonaCustomLabel={handlePersonaCustomLabel} />
        )
      case 'Engagement':
        return <Engagement handlePersonaCustomLabel={handlePersonaCustomLabel} />
      case 'Outreach':
        return (
          <Outreach filteredPersonaDesc={filteredPersonaDesc} handlePersonaCustomLabel={handlePersonaCustomLabel} />
        )
      default:
        return null
    }
  }

  const filteredPersonaDesc = PersonaDesc.filter((item) => item.name === personaSidebarOpen?.name)

  return (
    <Root ref={selectedPersonaRef} tabIndex={-1} aria-label='Selected Persona Section'>
      <CustomBox className={classes.summaryCustomBox}>
        {/* Header component here... */}
        <PersonaHeader
          personaSidebarOpen={personaSidebarOpen}
          setPersonaSidebarOpen={setPersonaSidebarOpen}
          filteredPersonaDesc={filteredPersonaDesc}
        />
        {/* Dropdown section here... */}
        <Select
          value={personaSection}
          MenuProps={{PaperProps: {sx: {mt: 0.5}}}}
          onChange={(e) => {
            setPersonaSection(e.target.value)
            AnalyticsHandler.onPersonaSelect({
              name: personaSidebarOpen?.name,
              section: e.target.value
            })
          }}
          className={classesIP.personaSelectCustom}
          IconComponent={ExpandMoreIcon}
        >
          <MenuItem value={'Overview'}>{handlePersonaCustomLabel('overview', null, 'Overview')}</MenuItem>
          <MenuItem value={'Engagement'}>{handlePersonaCustomLabel('engagement', null, 'Engagement')}</MenuItem>
          <MenuItem value={'Outreach'}>{handlePersonaCustomLabel('outreach', null, 'Outreach')}</MenuItem>
        </Select>

        {/* Render the selected section */}
        {renderContent()}
        <ChatbotCTA />
        <StyledSpaceBox aria-hidden='true' />
      </CustomBox>
    </Root>
  )
}

export default SelectedPersona

/*
Component Understanding: SelectedPersona
The SelectedPersona component is responsible for managing and displaying the details of a selected persona, likely including user interactions and state updates.
such as overview information, engagement metrics, and outreach strategies.

Function Descriptions:
  - ChatbotCTA: Renders a call-to-action for the chatbot, allowing users to interact with the chatbot for insights related to the persona.
  - handleSelectPersona: Handles the selection of a persona and updates the state accordingly.
  - fetchPersonaDetails: Fetches detailed information about the selected persona from an API or data source.
  - updatePersonaState: Updates the component's state with new persona data.
  - resetPersona: Resets the selected persona to its default state.
  - renderPersonaDetails: Renders the UI for displaying the selected persona's details.

*/
